import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Moon, SunDim } from "@phosphor-icons/react";

import { LocaleSwitcher } from "./LocaleSwitcher";
import { useThemeMode } from "../providers/ThemeProvider";
import { useAuth } from "../providers/AuthProvider";
import { defaultIconProps } from "../features/shared/ui/icons";
import { createLogger } from "../utils/logger";

const logger = createLogger("SiteFooter");

type SiteFooterVariant = "standalone" | "embedded" | "inline";

interface SiteFooterProps {
  variant?: SiteFooterVariant;
  className?: string;
  showSignout?: boolean;
}

export function SiteFooter({
  variant = "standalone",
  className,
  showSignout = true
}: SiteFooterProps) {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const { theme, setTheme } = useThemeMode();
  const { logout } = useAuth();

  const nextTheme = theme === "dark" ? "light" : "dark";
  const ThemeIcon = nextTheme === "light" ? SunDim : Moon;

  const baseClasses =
    "w-full rounded-3xl border border-slate-200 bg-white/95 px-4 py-6 pb-safe shadow-md backdrop-blur supports-[backdrop-filter]:bg-white/90 flex flex-col gap-4 text-slate-700 dark:border-slate-800 dark:bg-slate-900/95 dark:text-slate-100 sm:px-6 sm:py-8 lg:flex-row lg:items-center lg:justify-between";
  const embeddedClasses =
    "border-none bg-transparent px-0 py-0 shadow-none text-slate-600 dark:text-slate-300 backdrop-blur-none";
  const inlineClasses =
    "w-full pb-safe text-slate-600 dark:text-slate-300 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between";
  const leftStackClasses =
    "flex flex-col gap-4 text-center text-sm text-slate-500 dark:text-slate-400 sm:flex-row sm:items-center sm:gap-6 sm:text-left";
  const navClasses =
    "flex flex-wrap justify-center gap-4 text-sm text-slate-500 dark:text-slate-400 sm:justify-start";
  const navLinkClasses =
    "rounded-md px-1 font-medium transition-colors hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 dark:hover:text-slate-200";
  const rightStackClasses =
    "flex flex-wrap items-center justify-center gap-3 text-sm text-slate-600 dark:text-slate-300 sm:justify-start lg:ml-auto";
  const footerClasses =
    variant === "inline"
      ? [inlineClasses, className].filter(Boolean).join(" ")
      : [baseClasses, variant === "embedded" ? embeddedClasses : "", className]
          .filter(Boolean)
          .join(" ");

  return (
    <footer className={footerClasses}>
      <div className={leftStackClasses}>
        <span className="font-semibold text-slate-700">
          {t("site_footer.brand", { year })}
        </span>
        <nav className={navClasses}>
          <Link to="/legal/terms" className={navLinkClasses}>
            {t("site_footer.links.terms")}
          </Link>
          <Link to="/legal/privacy" className={navLinkClasses}>
            {t("site_footer.links.privacy")}
          </Link>
          <Link to="/legal/cookies" className={navLinkClasses}>
            {t("site_footer.links.cookies")}
          </Link>
        </nav>
      </div>
      <div className={rightStackClasses}>
        <LocaleSwitcher className="pm-btn pm-btn-secondary pm-btn-sm" />
        <button
          type="button"
          className="pm-btn pm-btn-secondary pm-btn-sm"
          onClick={() => setTheme(nextTheme)}
          aria-label={t("site_footer.theme_toggle.aria", {
            mode: t(`site_footer.theme_toggle.mode.${nextTheme}`),
         })}
        >
          <ThemeIcon {...defaultIconProps} aria-hidden />
          {t(`site_footer.theme_toggle.button.${nextTheme}`)}
        </button>
        {showSignout ? (
          <button
            type="button"
            className="pm-btn pm-btn-ghost pm-btn-sm"
            onClick={() => {
              logout().catch((error) => {
                logger.warn("Logout request failed", error);
              });
            }}
          >
            {t("auth.signout.button")}
          </button>
        ) : null}
      </div>
    </footer>
  );
}
